import { BeatResolution, Scale } from '@arpeggi/shared'
import { Arp2Utils } from '../../client/SongUtils'
import { genNotesProbabilistically, MINOR_SCALE } from '../GeneratorUtils'
import { MelodicGenerator } from '../MelodicGenerator'
import { RandIndexWithDistributions, RandProb, RandRange } from '../RandomUtils'

// Probabilities
export const HIGHEST = 0.7
export const HI = 0.5 // probability of note being active
export const MED = 0.2
export const LOW = 0.05

// Bass config
const BASS_BEAT_PROBS = [1, LOW, LOW, MED, LOW, LOW, HI, MED, LOW, MED, MED, HI, MED, MED, LOW, MED]

const BASS_NOTE_PROBS = [
  2, // root
  0.025, // second
  0.15, // third
  0.025, // fourth
  0.25, // fifth
  0.03, // sixth
  0.03, // seventh
  0.1 // octave
]

// Melody config
const MELODY_BEAT_PROBS = [
  MED,
  LOW,
  MED,
  LOW,
  MED,
  LOW,
  HI,
  MED,

  MED,
  MED,
  MED,
  HI,
  MED,
  MED,
  HI,
  MED
]

const MELODY_NOTE_PROBS = [
  0.75, // root
  0.025, // second
  0.15, // third
  0.025, // fourth
  0.25, // fifth
  0.03, // sixth
  0.03, // seventh
  0.2 // octave
]

// The classic Arpeggi Trap sound
export class TrapMelodicGenerator implements MelodicGenerator {
  // For now, let's use the interval algo for lead
  generateLeadLoop(loop: any /* MelodicLoopStore */) {
    const resolution = BeatResolution.EIGHTH_NOTE
    const length = Arp2Utils.getLengthOfRes(resolution)
    let notes: Array<number> = Array(length).fill(0)

    notes = TrapMelodicGenerator.generateMelodyIntervalAlgo(notes)

    loop.settings.updateResolution(resolution)
    loop.populateFromNumberArray(notes, Scale.MINOR)
  }

  // And use the probabalistic algo for harmony
  generateHarmonyLoop(loop: any /* MelodicLoopStore */) {
    const resolution = BeatResolution.EIGHTH_NOTE
    const length = Arp2Utils.getLengthOfRes(resolution)
    let notes: Array<number> = Array(length).fill(0)

    genNotesProbabilistically(notes, MELODY_BEAT_PROBS, MELODY_NOTE_PROBS)

    loop.settings.updateResolution(resolution)
    loop.populateFromNumberArray(notes, Scale.MINOR)
  }

  generateBassLoop(loop: any /* MelodicLoopStore */) {
    const resolution = BeatResolution.EIGHTH_NOTE
    const length = Arp2Utils.getLengthOfRes(resolution)

    let notes: Array<number> = Array(length).fill(0)
    notes[0] = 1

    genNotesProbabilistically(notes, BASS_BEAT_PROBS, BASS_NOTE_PROBS)

    loop.settings.updateResolution(resolution)
    loop.populateFromNumberArray(notes, Scale.MINOR)
  }

  // This uses a slightly more clever "interval" system to generate a better sounding melody
  static generateMelodyIntervalAlgo(notes: number[]) {
    const scaleArray = MINOR_SCALE

    // Random interval, in 1/8 notes, between 0 and 5
    var interval = RandRange(1, 5)

    // Random starting 1/4 note, must start before 2.5th beat:
    var start = RandRange(0, 2) * 2

    // do a quick assertion: if we have a syncopated interval (3 or 5 number), then force to be on first or third beat.
    // Otherwise shit sounds really weird.
    if ((interval === 3 || interval === 5) && start % 2 === 1) {
      start--
    }

    // Random number of notes in the melody. Actual value
    var targetNumber = RandRange(4, 8)

    var nonRepeating = true
    var probabilistic = false
    var halfMeasureMax = true

    // Non-repeating?
    var notesUsedAlready: Set<number> = new Set<number>()

    var currentBeat = start
    var attempts = 0
    var lastNoteBeat = 0

    for (var i = 0; i < targetNumber; i++) {
      // Add first note to melody.
      var currentNote = 0
      if (probabilistic) {
        do {
          currentNote = RandIndexWithDistributions(MELODY_NOTE_PROBS)
          attempts++
        } while (notesUsedAlready.has(currentNote) && attempts < 100)
      } else {
        currentNote = RandRange(0, scaleArray.length - 1)
      }
      if (nonRepeating) {
        notesUsedAlready.add(currentNote)
      }
      notes[currentBeat] = scaleArray[currentNote]

      lastNoteBeat = currentBeat
      currentBeat += interval
      if (currentBeat >= notes.length || (halfMeasureMax && currentBeat >= notes.length / 2)) {
        break
      }
    }
    // Decide if should duplicate this melody for the second half or not
    if (lastNoteBeat < notes.length) {
      var firstHalf = notes.slice(0, notes.length / 2)
      notes = firstHalf.concat(this.mutateMelodyPart(firstHalf, scaleArray))
    }
    return notes
  }

  // Given a section of a melody, randomly remove or change notes
  static mutateMelodyPart(notes: number[], currentScale: number[]): number[] {
    // in most cases, don't change.
    if (RandProb(0.6)) return notes

    var mutated = Object.assign([], notes)
    var notesToMutate = RandRange(0, 3, true)

    // Prioritize the end of the melody.
    for (var i = mutated.length - 1; i >= 0; i--) {
      if (mutated[i] === 0) continue
      if (notesToMutate > 0) {
        if (RandProb(0.8)) {
          mutated[i] = currentScale[RandIndexWithDistributions(MELODY_NOTE_PROBS)]
        } else {
          mutated[i] = 0
        }
        notesToMutate--
      }
    }
    return mutated
  }
}
